import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "../../hooks/useConfirm";
import { BookOpen, Plus, Search, Filter } from "lucide-react";
import { Tags } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import StatistichePreparazioni from "../../components/ricette/StatistichePreparazioni";
import { calcolaCostoIngrediente, udmKind, udmStorageFactor } from "../../utils/udmUtils";
import { useNavigate, useSearchParams } from "react-router-dom";

import { RicettaCard } from "../../components/ricette/RicettaCard";
import { ComposizioneDialog } from "../../components/ricette/ComposizioneDialog";
import { AggiungiComponenteDialog } from "../../components/ricette/AggiungiComponenteDialog";
import { VisualizzaRicettaDialog } from "../../components/ricette/VisualizzaRicettaDialog";
import NuovaRicettaWizard from "../../components/ricette/NuovaRicettaWizard";
import VerificaRicettaDialog from "../../components/ricette/VerificaRicettaDialog";
import GestioneCategorie from "./GestioneCategorie";
import { useCostAlertMaps } from "../../components/CostAlerts";
import StoricoVariazioniDialog from "../../components/StoricoVariazioniDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTempo = (minuti) => {
  if (!minuti || minuti <= 0) return null;
  if (minuti < 60) return `${minuti} min`;
  const h = Math.floor(minuti / 60);
  const m = minuti % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
};

const round2 = (n) => Math.round(n * 100) / 100;

const Ricette = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getAuthHeaders, localeCorrente, canManageRicette, isAdmin } = useAuth();
  const confirm = useConfirm();
  const isGestione = canManageRicette && (isAdmin || searchParams.get("gestione") === "1");
  const [loading, setLoading] = useState(true);
  const [ricette, setRicette] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [categorieIngredienti, setCategorieIngredienti] = useState([]);
  const [ingredientiDisponibili, setIngredientiDisponibili] = useState([]);
  const [search, setSearch] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("tutte");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [verificaDialogOpen, setVerificaDialogOpen] = useState(false);
  const [ricettaDaVerificare, setRicettaDaVerificare] = useState(null);
  const [categorieDialogOpen, setCategorieDialogOpen] = useState(false);
  const { ricetteById: alertMap } = useCostAlertMaps(localeCorrente?.id);
  const [storicoItem, setStoricoItem] = useState(null);

  const [form, setForm] = useState({ nome: "", categoria_id: "", ingredienti: [], tempo_preparazione: "", note: "" });
  const [addIngredienteOpen, setAddIngredienteOpen] = useState(false);
  const [tipoComponente, setTipoComponente] = useState("ingrediente");
  const [nuovoIngrediente, setNuovoIngrediente] = useState({ ingrediente_id: "", ricetta_id: "", quantita: "", peso_lordo: "", percentuale_scarto: 0, cotto: false, calo_cottura_percentuale: 0, is_acqua: false });
  const [scartiCache, setScartiCache] = useState({});

  useEffect(() => {
    if (localeCorrente?.id) fetchData();
  }, [localeCorrente?.id]);

  const fetchData = async () => {
    if (!localeCorrente?.id) return;
    try {
      await axios.post(`${API}/categorie-ricette/init?locale_id=${localeCorrente.id}`, {}, { headers: getAuthHeaders() });
      const [ricetteRes, categorieRes, ingredientiRes, categorieIngRes] = await Promise.all([
        axios.get(`${API}/ricette?locale_id=${localeCorrente.id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/categorie-ricette?locale_id=${localeCorrente.id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/ingredienti?locale_id=${localeCorrente.id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/categorie-ingredienti?locale_id=${localeCorrente.id}`, { headers: getAuthHeaders() })
      ]);
      setRicette(ricetteRes.data);
      setCategorie(categorieRes.data);
      setIngredientiDisponibili(ingredientiRes.data);
      setCategorieIngredienti(categorieIngRes.data);
    } catch { toast.error("Errore nel caricamento dei dati"); }
    finally { setLoading(false); }
  };

  const openDialog = (item = null) => {
    if (item) {
      setEditItem(item);
      setForm({
        nome: item.nome || "", categoria_id: item.categoria_id || "",
        ingredienti: (item.ingredienti || []).map(ing => ({ ...ing, peso_lordo: ing.peso_lordo != null ? ing.peso_lordo : ing.quantita })),
        tempo_preparazione: item.tempo_preparazione != null ? String(item.tempo_preparazione) : "",
        note: item.note || "", richiede_tracciabilita: item.richiede_tracciabilita || false,
        peso_finale_kg: item.peso_finale_kg != null && item.peso_finale_kg > 0 ? String(item.peso_finale_kg) : ""
      });
    } else {
      setEditItem(null);
      setForm({ nome: "", categoria_id: "", ingredienti: [], tempo_preparazione: "", note: "", richiede_tracciabilita: true });
    }
    setDialogOpen(true);
  };

  const handleCardClick = (item) => isGestione ? openDialog(item) : (() => { setViewItem(item); setViewDialogOpen(true); })();

  // Deep-link dagli alert: /anagrafica/ricette?open={id} apre e scrolla alla ricetta
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || !ricette.length) return;
    const item = ricette.find((r) => r.id === openId);
    if (!item) return;
    handleCardClick(item);
    setTimeout(() => {
      document.querySelector(`[data-testid="ricetta-card-${openId}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ricette, searchParams]);
  const openViewDialog = (item) => { setViewItem(item); setViewDialogOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) { toast.error("Nome ricetta obbligatorio"); return; }
    if (form.ingredienti.length === 0) { toast.error("Aggiungi almeno un ingrediente"); return; }
    setSaving(true);
    try {
      const tempoPrep = form.tempo_preparazione !== "" ? parseInt(form.tempo_preparazione, 10) : null;
      const payload = { locale_id: localeCorrente.id, nome: form.nome, categoria_id: form.categoria_id || null, ingredienti: form.ingredienti, tempo_preparazione: isNaN(tempoPrep) ? null : tempoPrep, note: form.note || null, richiede_tracciabilita: form.richiede_tracciabilita || false };
      const pesoFinaleKg = form.peso_finale_kg !== "" && form.peso_finale_kg != null ? parseFloat(form.peso_finale_kg) : null;
      if (pesoFinaleKg && pesoFinaleKg > 0) payload.peso_finale_kg = pesoFinaleKg;
      if (form.is_light === false && editItem?.is_light === true) payload.is_light = false;
      if (editItem) { await axios.put(`${API}/ricette/${editItem.id}`, payload, { headers: getAuthHeaders() }); toast.success("Ricetta aggiornata"); }
      else { await axios.post(`${API}/ricette`, payload, { headers: getAuthHeaders() }); toast.success("Ricetta creata"); }
      setDialogOpen(false);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!await confirm("Sei sicuro di voler eliminare questa ricetta?")) return;
    try { await axios.delete(`${API}/ricette/${id}`, { headers: getAuthHeaders() }); toast.success("Ricetta eliminata"); fetchData(); }
    catch (error) { toast.error(error.response?.data?.detail || "Errore nell'eliminazione"); }
  };

  const handleDeleteProcedura = async (ricettaId) => {
    if (!await confirm("Sei sicuro di voler cancellare la procedura a step di questa ricetta?", { confirmLabel: "Cancella" })) return;
    try { await axios.delete(`${API}/ricette/${ricettaId}/procedura`, { headers: getAuthHeaders() }); toast.success("Procedura cancellata"); fetchData(); }
    catch { toast.error("Errore nella cancellazione della procedura"); }
  };

  const handleDownloadPdf = async (item) => {
    try {
      toast.info("Generazione PDF in corso...");
      const res = await axios.get(`${API}/ricette/${item.id}/procedura-pdf`, { headers: getAuthHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Ricetta_${(item.nome || "ricetta").replace(/[^A-Za-z0-9_-]+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore nel generare il PDF"); }
  };

  const handleDuplica = (item) => {
    setEditItem(null);
    setForm({
      nome: `${item.nome} (copia)`,
      categoria_id: item.categoria_id || "",
      ingredienti: (item.ingredienti || []).map(ing => ({ ...ing, peso_lordo: ing.peso_lordo != null ? ing.peso_lordo : ing.quantita })),
      tempo_preparazione: item.tempo_preparazione != null ? String(item.tempo_preparazione) : "",
      note: item.note || "",
      richiede_tracciabilita: item.richiede_tracciabilita || false
    });
    setDialogOpen(true);
    toast.info("Modifica il nome e salva la copia");
  };

  const toggleTracciabilita = async (ricettaId, currentValue) => {
    try {
      await axios.put(`${API}/ricette/${ricettaId}`, { richiede_tracciabilita: !currentValue }, { headers: getAuthHeaders() });
      setRicette(prev => prev.map(r => r.id === ricettaId ? { ...r, richiede_tracciabilita: !currentValue } : r));
      toast.success(!currentValue ? "Tracciabilità lotti attivata" : "Tracciabilità lotti disattivata");
    } catch { toast.error("Errore nell'aggiornamento"); }
  };

  const handleWizardSave = async (data) => {
    try {
      await axios.post(`${API}/ricette`, { locale_id: localeCorrente.id, ...data }, { headers: getAuthHeaders() });
      toast.success("Ricetta creata! Ricorda di verificarla dopo la cottura.");
      setWizardOpen(false);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Errore nella creazione"); }
  };

  const openVerificaDialog = (ricetta) => { setRicettaDaVerificare(ricetta); setVerificaDialogOpen(true); };

  const handleVerificaRicetta = async (data) => {
    try {
      await axios.post(`${API}/ricette/${ricettaDaVerificare.id}/verifica`, data, { headers: getAuthHeaders() });
      toast.success("Ricetta verificata con successo!");
      setVerificaDialogOpen(false);
      setRicettaDaVerificare(null);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Errore nella verifica"); }
  };

  // Ingrediente management
  const addIngrediente = () => {
    if (tipoComponente === "ricetta") {
      if (!nuovoIngrediente.ricetta_id) { toast.error("Seleziona una ricetta"); return; }
      if (!nuovoIngrediente.quantita || parseFloat(nuovoIngrediente.quantita) <= 0) { toast.error("Inserisci una quantità valida"); return; }
      const ricettaDb = ricette.find(r => r.id === nuovoIngrediente.ricetta_id);
      if (!ricettaDb) { toast.error("Ricetta non trovata"); return; }
      if (editItem && ricettaDb.id === editItem.id) { toast.error("Non puoi aggiungere questa ricetta a se stessa"); return; }
      const quantitaGrammi = parseFloat(nuovoIngrediente.quantita) || 0;
      const costoPerKg = ricettaDb.costo_per_kg || 0;
      setForm(prev => ({ ...prev, ingredienti: [...prev.ingredienti, { ingrediente_id: "", ingrediente_nome: ricettaDb.nome, tipo: "ricetta", ricetta_id: ricettaDb.id, quantita: quantitaGrammi, unita_misura: "g", cotto: false, calo_cottura_percentuale: 0, is_acqua: false, prezzo_usato: costoPerKg, costo_ingrediente: (quantitaGrammi / 1000) * costoPerKg }] }));
      setNuovoIngrediente({ ingrediente_id: "", ricetta_id: "", quantita: "", peso_lordo: "", percentuale_scarto: 0, cotto: false, calo_cottura_percentuale: 0, is_acqua: false });
      setTipoComponente("ingrediente");
      setAddIngredienteOpen(false);
      return;
    }
    if (!nuovoIngrediente.ingrediente_id && !nuovoIngrediente.is_acqua) { toast.error("Seleziona un ingrediente"); return; }
    if (!nuovoIngrediente.quantita || parseFloat(nuovoIngrediente.quantita) <= 0) { toast.error("Inserisci una quantità valida"); return; }
    const ingredienteDb = ingredientiDisponibili.find(i => i.id === nuovoIngrediente.ingrediente_id);
    // OPZIONE (b): l'utente inserisce nell'UDM del prodotto → converti in unità piccola (g/ml) per lo storage.
    const factor = nuovoIngrediente.is_acqua ? 1 : udmStorageFactor(ingredienteDb?.unita_misura || "kg");
    const pesoNetto = parseFloat(nuovoIngrediente.quantita) * factor;
    const pesoLordo = nuovoIngrediente.peso_lordo ? parseFloat(nuovoIngrediente.peso_lordo) * factor : null;
    const percScarto = pesoLordo && pesoLordo > pesoNetto ? round2((pesoLordo - pesoNetto) / pesoLordo * 100) : (parseFloat(nuovoIngrediente.percentuale_scarto) || 0);
    setForm(prev => ({ ...prev, ingredienti: [...prev.ingredienti, { ingrediente_id: nuovoIngrediente.is_acqua ? "" : nuovoIngrediente.ingrediente_id, ingrediente_nome: nuovoIngrediente.is_acqua ? "Acqua" : ingredienteDb?.nome || "Sconosciuto", tipo: "ingrediente", quantita: pesoNetto, peso_lordo: pesoLordo || pesoNetto, percentuale_scarto: percScarto, unita_misura: nuovoIngrediente.is_acqua ? "litro" : (ingredienteDb?.unita_misura || "kg"), cotto: nuovoIngrediente.cotto, calo_cottura_percentuale: nuovoIngrediente.cotto ? parseFloat(nuovoIngrediente.calo_cottura_percentuale) || 0 : 0, is_acqua: nuovoIngrediente.is_acqua, prezzo_usato: ingredienteDb?.prezzo_attuale || 0, costo_ingrediente: 0 }] }));
    setNuovoIngrediente({ ingrediente_id: "", ricetta_id: "", quantita: "", peso_lordo: "", percentuale_scarto: 0, cotto: false, calo_cottura_percentuale: 0, is_acqua: false });
    setAddIngredienteOpen(false);
  };

  const updateIngredienteField = (index, field, value) => {
    setForm(prev => {
      const updated = [...prev.ingredienti];
      const ing = { ...updated[index] };
      const numVal = value === "" ? "" : parseFloat(value) || 0;
      ing[field] = numVal;
      if (field === "peso_lordo") {
        const lordo = parseFloat(numVal) || 0;
        const netto = parseFloat(ing.quantita) || 0;
        ing.percentuale_scarto = (lordo > 0 && netto > 0 && lordo >= netto) ? Math.round((lordo - netto) / lordo * 1000) / 10 : 0;
      } else if (field === "quantita") {
        const lordo = parseFloat(ing.peso_lordo) || 0;
        const netto = parseFloat(numVal) || 0;
        if (lordo > 0 && netto > 0 && lordo >= netto) ing.percentuale_scarto = Math.round((lordo - netto) / lordo * 1000) / 10;
        else if (lordo === 0) ing.percentuale_scarto = 0;
      }
      updated[index] = ing;
      return { ...prev, ingredienti: updated };
    });
  };

  const moveIngredienteUp = (index) => {
    if (index === 0) return;
    setForm(prev => { const arr = [...prev.ingredienti]; [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]]; return { ...prev, ingredienti: arr }; });
  };
  const moveIngredienteDown = (index) => {
    if (index === form.ingredienti.length - 1) return;
    setForm(prev => { const arr = [...prev.ingredienti]; [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]]; return { ...prev, ingredienti: arr }; });
  };

  const calcolaCosti = () => {
    let costoTotale = 0, pesoFinale = 0;
    form.ingredienti.forEach(ing => {
      const ingredienteDb = ingredientiDisponibili.find(i => i.id === ing.ingrediente_id);
      const prezzoBase = ingredienteDb?.prezzo_attuale || ing.prezzo_usato || 0;
      const udm = ingredienteDb?.unita_misura || ing.unita_misura || "kg";
      const quantitaNetta = parseFloat(ing.quantita) || 0;
      const pesoLordo = parseFloat(ing.peso_lordo) || quantitaNetta;
      const caloCottura = ing.cotto ? (ing.calo_cottura_percentuale || 0) : 0;

      // I quantitativi sono SEMPRE salvati in g/ml/pezzi. Nessuna conversione ×1000.
      // I pezzi non concorrono al peso del piatto (non convertibili in grammi).
      const qtyInGrammi = udmKind(udm) === "pezzi" ? 0 : quantitaNetta;
      pesoFinale += qtyInGrammi * (1 - caloCottura / 100);

      if (!ing.is_acqua) {
        costoTotale += calcolaCostoIngrediente(pesoLordo, prezzoBase, udm);
      }
    });
    const pesoFinaleKg = pesoFinale / 1000;
    const costoPerKg = pesoFinaleKg > 0 ? costoTotale / pesoFinaleKg : 0;
    return { pesoFinaleGrammi: Math.round(pesoFinale), pesoFinaleKg: pesoFinaleKg.toFixed(3), costoTotale: costoTotale.toFixed(2), costoPerKg: costoPerKg.toFixed(2) };
  };

  const getCategoriaNome = (id) => categorie.find(c => c.id === id)?.nome || "Senza categoria";
  const costi = calcolaCosti();

  const ricetteFiltrate = ricette.filter(r => {
    const matchSearch = !search || r.nome.toLowerCase().includes(search.toLowerCase());
    const matchCategoria = filtroCategoria === "tutte" || r.categoria_id === filtroCategoria;
    return matchSearch && matchCategoria;
  });

  if (loading) return <div className="loading-state"><div className="text-muted-custom">Caricamento...</div></div>;

  return (
    <div className="page-container" data-testid="ricette-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Ricette</h1>
          <p className="page-subtitle">{isGestione ? "Gestisci le ricette con calcolo costi automatico" : "Consulta le ricette"}</p>
        </div>
        {isGestione && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setCategorieDialogOpen(true)} className="btn-secondary" data-testid="gestione-categorie-btn">
              <Tags className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Categorie</span>
            </Button>
            <Button onClick={() => setWizardOpen(true)} className="btn-primary" data-testid="add-ricetta-btn">
              <Plus className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Nuova Ricetta</span><span className="sm:hidden">Nuovo</span>
            </Button>
          </div>
        )}
      </div>

      <Tabs defaultValue="catalogo" className="w-full">
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="catalogo" data-testid="tab-catalogo">Catalogo</TabsTrigger>
          <TabsTrigger value="preparazioni" data-testid="tab-preparazioni">Statistiche preparazioni</TabsTrigger>
        </TabsList>

        <TabsContent value="catalogo" className="space-y-4 mt-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Cerca ricetta..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-card border-border text-foreground" />
            </div>
            <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
              <SelectTrigger className="w-full sm:w-48 bg-card border-border text-foreground">
                <Filter className="h-4 w-4 mr-2" /><SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="tutte" className="text-foreground">Tutte le categorie</SelectItem>
                {categorie.map(c => (<SelectItem key={c.id} value={c.id} className="text-foreground">{c.nome}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-3">
            {ricetteFiltrate.length === 0 ? (
              <div className="empty-state"><BookOpen className="empty-state-icon" /><p className="empty-state-text">Nessuna ricetta trovata</p></div>
            ) : (
              ricetteFiltrate.map((item) => (
                <RicettaCard
                  key={item.id}
                  item={item}
                  isGestione={isGestione}
                  isAdmin={isAdmin}
                  getCategoriaNome={getCategoriaNome}
                  formatTempo={formatTempo}
                  onCardClick={handleCardClick}
                  onEdit={openDialog}
                  onDuplica={handleDuplica}
                  onDelete={handleDelete}
                  onDeleteProcedura={handleDeleteProcedura}
                  onVerifica={openVerificaDialog}
                  onToggleTracciabilita={toggleTracciabilita}
                  alert={alertMap[item.id]}
                  onOpenStorico={(it) => setStoricoItem(it)}
                  onDownloadPdf={handleDownloadPdf}
                />
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="preparazioni" className="mt-4">
          <StatistichePreparazioni localeId={localeCorrente?.id} getAuthHeaders={getAuthHeaders} />
        </TabsContent>
      </Tabs>

      <ComposizioneDialog open={dialogOpen} onOpenChange={setDialogOpen} editItem={editItem} form={form} onFormChange={setForm} saving={saving} categorie={categorie} ingredientiDisponibili={ingredientiDisponibili} ricette={ricette} isAdmin={isAdmin} onSubmit={handleSubmit} onAddIngrediente={() => setAddIngredienteOpen(true)} onRemoveIngrediente={(idx) => setForm(prev => ({ ...prev, ingredienti: prev.ingredienti.filter((_, i) => i !== idx) }))} onUpdateField={updateIngredienteField} onMoveUp={moveIngredienteUp} onMoveDown={moveIngredienteDown} costi={costi} formatTempo={formatTempo} />
      <AggiungiComponenteDialog open={addIngredienteOpen} onOpenChange={setAddIngredienteOpen} tipoComponente={tipoComponente} onTipoChange={setTipoComponente} nuovoIngrediente={nuovoIngrediente} onNuovoIngredienteChange={setNuovoIngrediente} ingredientiDisponibili={ingredientiDisponibili} categorieIngredienti={categorieIngredienti} ricette={ricette} editItem={editItem} scartiCache={scartiCache} onScartiCacheUpdate={setScartiCache} getAuthHeaders={getAuthHeaders} onAdd={addIngrediente} />
      <VisualizzaRicettaDialog open={viewDialogOpen} onOpenChange={setViewDialogOpen} item={viewItem} isAdmin={isAdmin} getCategoriaNome={getCategoriaNome} formatTempo={formatTempo} onVerifica={openVerificaDialog} />
      <NuovaRicettaWizard open={wizardOpen} onOpenChange={setWizardOpen} ingredienti={ingredientiDisponibili} categorie={categorie} categorieIngredienti={categorieIngredienti} onSave={handleWizardSave} />
      <VerificaRicettaDialog open={verificaDialogOpen} onOpenChange={setVerificaDialogOpen} ricetta={ricettaDaVerificare} onVerifica={handleVerificaRicetta} showCosts={isAdmin} />
      <Dialog open={categorieDialogOpen} onOpenChange={setCategorieDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto"><DialogHeader><DialogTitle>Gestione Categorie</DialogTitle></DialogHeader><GestioneCategorie embedded onCategorieChange={fetchData} /></DialogContent>
      </Dialog>
      <StoricoVariazioniDialog
        open={!!storicoItem}
        onOpenChange={(o) => { if (!o) setStoricoItem(null); }}
        itemId={storicoItem?.id}
        itemNome={storicoItem?.nome}
        tipo="ricetta"
      />
    </div>
  );
};

export default Ricette;
